import { createEnv as validate } from '@t3-oss/env-core';
import type { ZodType, z } from 'zod';

/**
 * Typed, validated environment variables - the single seam where env policy lives, and the only
 * place `@t3-oss/env-core` is imported. Server vars are declared once and read from `process.env`;
 * browser-facing vars additionally need a `clientRuntimeEnv` entry.
 *
 * Validation always runs, and empty strings count as missing. Runs allowed to proceed without real
 * secrets - tests, and `SKIP_ENV_VALIDATION` for schema-only tasks - get a relaxed schema rather
 * than a skipped one, so `extends` presets, defaults and coercion behave as they do in production.
 * Skipping those outright is how a CI-only guard once fell through into a crash.
 */
export function createEnv<
  TServer extends Dictionary = EmptyDictionary,
  TClient extends Dictionary = EmptyDictionary,
  const TExtends extends Presets = [],
>(options: Options<TServer, TClient, TExtends>): Env<TServer, TClient, TExtends> {
  // Guarded because this also runs in client bundles, where Vite leaves `process` undefined and
  // expects `import.meta.env`. Client vars come from `clientRuntimeEnv` regardless, so having
  // nothing ambient is fine.
  const ambient: Record<string, string | undefined> = typeof process === 'undefined' ? {} : process.env;
  const lenient = ambient.NODE_ENV === 'test' || !!ambient.SKIP_ENV_VALIDATION;
  const { server, client, clientRuntimeEnv } = options as {
    server?: Dictionary;
    client?: Dictionary;
    clientRuntimeEnv?: Record<string, unknown>;
  };

  // Relaxing is runtime-only: callers keep the strict types their schema declares.
  return validate({
    ...options,
    clientPrefix: client && CLIENT_PREFIX,
    // Spread rather than hand over the environment itself: empty-string stripping deletes keys from
    // whatever object it is given, and mutating the real environment is not ours to do.
    runtimeEnv: { ...ambient, ...clientRuntimeEnv },
    ...(lenient ? { server: relax(server), client: relax(client) } : {}),
    emptyStringAsUndefined: true,
  } as Parameters<typeof validate>[0]) as Env<TServer, TClient, TExtends>;
}

/** Widen vars that would fail on a missing value. Anything already accepting `undefined` is left
 * alone - including vars with a `.default()`, so their defaults still apply. */
function relax(shape: Dictionary | undefined): Dictionary | undefined {
  if (!shape) return shape;
  return Object.fromEntries(
    Object.entries(shape).map(([key, schema]) => [
      key,
      schema.safeParse(undefined).success ? schema : schema.optional(),
    ]),
  );
}

/** The prefix a build inlines into the client bundle. Vite's, which covers everything we build
 * with it, TanStack Start included. */
const CLIENT_PREFIX = 'VITE_';
type ClientPrefix = typeof CLIENT_PREFIX;
type AnyClientPrefixed = `${ClientPrefix}${string}`;

type Dictionary = Record<string, ZodType>;
type EmptyDictionary = NonNullable<unknown>;

/** Already-built env objects to inherit from, i.e. other packages' `keys()`. */
type Presets = Array<Record<string, unknown>>;

export type Options<TServer extends Dictionary, TClient extends Dictionary, TExtends extends Presets> = ServerOptions<
  TServer,
  TExtends
> &
  ClientOptions<TClient>;

interface ServerOptions<TServer extends Dictionary, TExtends extends Presets> {
  /** Server-only vars. Never sent to the browser, so they must carry no client prefix. */
  server?: Partial<{
    [TKey in keyof TServer]: TKey extends AnyClientPrefixed
      ? ErrorMessage<`${TKey & string} is server-only, so it must not carry a client prefix`>
      : TServer[TKey];
  }>;

  /** Env objects to merge in, letting a package inherit the vars it depends on. */
  extends?: TExtends;
}

/** A client var is only inlined if the build can see it declared, so the schema and the runtime
 * values come together or not at all. */
type ClientOptions<TClient extends Dictionary> =
  | { client?: never; clientRuntimeEnv?: never }
  | {
      /** Inlined into the client bundle. Public by definition - never put a secret here. */
      client: Partial<{
        [TKey in keyof TClient]: TKey extends AnyClientPrefixed
          ? TClient[TKey]
          : ErrorMessage<`${TKey & string} must be prefixed with ${ClientPrefix} to reach the browser`>;
      }>;

      /**
       * Unlike server vars these cannot come from the environment wholesale: a build inlines them
       * by substituting the literal text `import.meta.env.VITE_FOO`, so anything it cannot see
       * written out ends up `undefined` in the browser.
       */
      clientRuntimeEnv: Record<Extract<keyof TClient, string>, string | boolean | number | undefined>;
    };

/** This module's own vars, plus everything inherited via `extends`. */
export type Env<TServer extends Dictionary, TClient extends Dictionary, TExtends extends Presets> = Readonly<
  Simplify<Reduce<[UndefinedOptional<Infer<TServer & TClient>>, ...TExtends]>>
>;

type Infer<T extends Dictionary> = { [TKey in keyof T]: z.infer<T[TKey]> };

// Type plumbing, adapted from @t3-oss/env-core. `UndefinedOptional` is the load-bearing one: it
// turns vars that can be absent into optional properties, so reading one is `string | undefined`.
type ErrorMessage<T extends string> = T;
type Simplify<T> = { [P in keyof T]: T[P] } & {};
type PossiblyUndefinedKeys<T> = { [K in keyof T]: undefined extends T[K] ? K : never }[keyof T];
type UndefinedOptional<T> = Partial<Pick<T, PossiblyUndefinedKeys<T>>> & Omit<T, PossiblyUndefinedKeys<T>>;
type Mutable<T> = T extends Readonly<infer U> ? U : T;
type Reduce<TArr extends Array<Record<string, unknown>>, TAcc = object> = TArr extends []
  ? TAcc
  : TArr extends [infer Head, ...infer Tail]
    ? Tail extends Array<Record<string, unknown>>
      ? Mutable<Head> & Omit<Reduce<Tail, TAcc>, keyof Head>
      : never
    : never;
